/**
 * BAM IQ's light Markdown as React elements: paragraphs, headings, bullet and
 * numbered lists, bold, italics, [S#] source tags, and "try this" cards:
 *
 *   :::try 2v2 guard-to-guard
 *   Setup: ...
 *   :::
 *
 * Built as elements, not HTML strings, so nothing in a reply can inject
 * markup. A card still being written (no closing line yet) shows as a card.
 */
import React from "react";

function inline(text, renderTag, keyBase) {
  const out = [];
  const re = /(\*\*[^*]+\*\*|\*[^*\n]+\*|\[S\d{1,3}\])/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${keyBase}-${i++}`;
    if (tok.startsWith("**")) out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("[S")) out.push(renderTag ? renderTag(tok.slice(1, -1), k) : null);
    else out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const TRY_OPEN = /^\s*:::\s*try\b\s*(.*)$/i;
const TRY_CLOSE = /^\s*:::\s*$/;

/** Split out the try-this cards: [{ text } | { card: { title, text } }]. */
export function splitCards(text) {
  const out = [];
  let cur = null;
  let prose = [];
  const flushProse = () => { if (prose.length) { out.push({ text: prose.join("\n") }); prose = []; } };
  for (const line of String(text || "").split("\n")) {
    let m;
    if (!cur && (m = line.match(TRY_OPEN))) { flushProse(); cur = { title: m[1].trim(), lines: [] }; }
    else if (cur && TRY_CLOSE.test(line)) { out.push({ card: { title: cur.title, text: cur.lines.join("\n") } }); cur = null; }
    else if (cur) cur.lines.push(line);
    else prose.push(line);
  }
  if (cur) out.push({ card: { title: cur.title, text: cur.lines.join("\n") } });
  flushProse();
  return out;
}

export function renderMarkdown(text, opts = {}) {
  const parts = splitCards(text);
  if (parts.length === 1 && !parts[0].card) return renderBlocks(parts[0].text, opts, "");
  return parts.map((p, i) => (p.card
    ? (opts.renderCard ? opts.renderCard(p.card.title, renderBlocks(p.card.text, opts, `c${i}-`), `card${i}`)
      : <div key={`card${i}`} data-try style={opts.styles?.card}><strong>{p.card.title}</strong>{renderBlocks(p.card.text, opts, `c${i}-`)}</div>)
    : <React.Fragment key={`t${i}`}>{renderBlocks(p.text, opts, `t${i}-`)}</React.Fragment>));
}

function renderBlocks(text, { renderTag, styles = {} } = {}, pre = "") {
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  String(text || "").split("\n").forEach((raw, n) => {
    const line = raw.trimEnd();
    let m;
    if ((m = line.match(/^#{1,6}\s+(.*)/))) {
      flush();
      blocks.push(<div key={`${pre}h${n}`} style={styles.heading}>{inline(m[1], renderTag, `${pre}h${n}`)}</div>);
    } else if ((m = line.match(/^\s*[-*]\s+(.*)/)) || (m = line.match(/^\s*\d+[.)]\s+(.*)/))) {
      const ordered = /^\s*\d/.test(line);
      if (!list || list.ordered !== ordered) { flush(); list = { ordered, items: [], key: `${pre}l${n}` }; }
      list.items.push(<li key={`${pre}i${n}`} style={styles.item}>{inline(m[1], renderTag, `${pre}i${n}`)}</li>);
    } else if (!line.trim()) {
      flush();
    } else {
      flush();
      // In a card, "Setup:", "Constraints:", "Watch for:" read as labels.
      const label = pre.startsWith("c") && line.match(/^([A-Z][A-Za-z '/-]{1,22}):\s+(.*)$/);
      blocks.push(label
        ? <p key={`${pre}p${n}`} style={styles.paragraph}><strong>{label[1]}:</strong> {inline(label[2], renderTag, `${pre}p${n}`)}</p>
        : <p key={`${pre}p${n}`} style={styles.paragraph}>{inline(line, renderTag, `${pre}p${n}`)}</p>);
    }
  });
  flush();
  return blocks.map(b => (b && b.items
    ? React.createElement(b.ordered ? "ol" : "ul", { key: b.key, style: styles.list }, b.items)
    : b));
}
